import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Mail, Paperclip, X, ChevronLeft, ChevronRight, ChevronDown, Send, Plus, Pencil, Trash2, Braces, FileText, Check, Loader2, AlertTriangle, Link2, Save, ExternalLink } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import UserAvatar from "@/components/shared/Avatar";
import { currentUser } from "@/data/dummy";
import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useAccounts } from "@/lib/hooks/use-accounts";
import {
  useSendEmailsMutation,
  useGetEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
} from "@/lib/graphql/types-and-hooks";
import { UserIntegrationPlatform } from "@/lib/graphql/types-and-hooks";
import { uploadEmailAttachments } from "@/lib/services/email-attachment-upload";
import DOMPurify from "dompurify";
import {
  appendSignatureToBody,
  stripSignatureMarker,
} from "@/lib/utils/email-signature";
import { useEffectiveEmailSignature } from "@/lib/hooks/use-effective-email-signature";
import { getDisplayName } from "@/lib/utils/display-name";
import { resolveMergeVars, MERGE_FIELD_DEFAULTS } from "@/lib/utils/merge-vars";
import InsertVariableDialog from "@/components/shared/InsertVariableDialog";
import { clearEmailDraft, getEmailDraft, recipientsDraftKey, saveEmailDraft } from "@/lib/utils/email-drafts";

export interface Recipient {
  id: string;
  name: string;
  email: string;
  title?: string;
  company?: string;
  phone?: string;
  location?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  isCustom: boolean;
  attachmentUrls?: string[];
}

// Preview mode has no backend, and this dialog is mounted by several pages at
// once — so saved templates are persisted to localStorage and broadcast so
// every instance (and every later open) sees them in the picker.
const PREVIEW_TEMPLATES_EVENT = "preview-email-templates-changed";
const previewTemplatesKey = (workspaceId?: string | null) =>
  `coffee.preview.emailTemplates.${workspaceId ?? "default"}`;

function readPreviewTemplates(workspaceId?: string | null): EmailTemplate[] {
  try {
    const raw = localStorage.getItem(previewTemplatesKey(workspaceId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePreviewTemplates(workspaceId: string | null | undefined, templates: EmailTemplate[]) {
  try {
    localStorage.setItem(previewTemplatesKey(workspaceId), JSON.stringify(templates));
  } catch {
    // Non-fatal: templates are a local convenience in preview mode
  }
  window.dispatchEvent(new CustomEvent(PREVIEW_TEMPLATES_EVENT));
}

/** Recipient field ids for drag & drop / "Move to" (ids, not labels). */
type RecipientField = "to" | "cc" | "bcc";
const FIELD = { to: "to", cc: "cc", bcc: "bcc" } as const satisfies Record<RecipientField, RecipientField>;

interface ComposeEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipients: Recipient[];
  /** Pre-fill the subject when the dialog opens (e.g. meeting follow-up). */
  initialSubject?: string;
  /** Pre-fill the body when the dialog opens (e.g. AI-drafted follow-up). */
  initialBody?: string;
  /**
   * Pre-fill the Cc list (e.g. internal meeting attendees). A pre-filled Cc
   * also defaults the send mode to one shared email; the user can switch it.
   */
  initialCcRecipients?: Recipient[];
  /**
   * Context a saved draft belongs to (e.g. a meeting follow-up). When the
   * dialog opens and a draft was saved for this context, it is restored in
   * place of the initial values. Defaults to the recipient set, so a draft
   * saved for a contact comes back the next time that contact is emailed.
   */
  draftKey?: string;
  /** Called after "Save Draft" stores the draft (e.g. to mirror it in the meeting Email tab). */
  onSaveDraft?: (draft: { subject: string; body: string }) => void;
}

/**
 * `key` is the merge-variable token shared with the backend and the agent
 * service ({{first_name}} and friends), so it is never translated; `labelKey`
 * is the human description shown beside it.
 */
type MergeField = {
  key: string;
  labelKey: string;
  resolve: (r: Recipient) => string;
  defaultFallback: string;
};

const MERGE_FIELDS: MergeField[] = [
  { key: "first_name", labelKey: "fields.people.firstName", resolve: (r) => r.name.split(" ")[0], defaultFallback: MERGE_FIELD_DEFAULTS.first_name },
  { key: "last_name", labelKey: "fields.people.lastName", resolve: (r) => r.name.split(" ").slice(1).join(" ") || r.name, defaultFallback: MERGE_FIELD_DEFAULTS.last_name },
  { key: "full_name", labelKey: "fields.people.name", resolve: (r) => r.name, defaultFallback: MERGE_FIELD_DEFAULTS.full_name },
  { key: "email", labelKey: "fields.people.email", resolve: (r) => r.email, defaultFallback: MERGE_FIELD_DEFAULTS.email },
  { key: "company", labelKey: "fields.people.company", resolve: (r) => r.company || "", defaultFallback: MERGE_FIELD_DEFAULTS.company },
  { key: "title", labelKey: "fields.people.title", resolve: (r) => r.title || "", defaultFallback: MERGE_FIELD_DEFAULTS.title },
  { key: "phone", labelKey: "fields.people.phone", resolve: (r) => r.phone || "", defaultFallback: MERGE_FIELD_DEFAULTS.phone },
  { key: "location", labelKey: "fields.people.location", resolve: (r) => r.location || "", defaultFallback: MERGE_FIELD_DEFAULTS.location },
  // Sender fields use dummy fallbacks; backend user overrides them at render time
  { key: "sender_full_name", labelKey: "forms.email.senderName", resolve: () => currentUser.fullName, defaultFallback: MERGE_FIELD_DEFAULTS.sender_full_name },
  { key: "sender_email", labelKey: "forms.email.senderEmail", resolve: () => currentUser.email, defaultFallback: MERGE_FIELD_DEFAULTS.sender_email },
  { key: "sender_company", labelKey: "forms.email.senderCompany", resolve: () => "", defaultFallback: MERGE_FIELD_DEFAULTS.sender_company },
];


function resolveTemplate(text: string, recipient: Recipient, senderOverrides?: Record<string, string>): string {
  const values: Record<string, string> = {};
  for (const f of MERGE_FIELDS) {
    const override = senderOverrides?.[f.key];
    values[f.key] = override !== undefined ? override : f.resolve(recipient);
  }
  return resolveMergeVars(text, values);
}
function VariableInsertMenu({ onPick }: { onPick: (field: MergeField) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground hover:bg-muted/50"
        onClick={() => setOpen(prev => !prev)}
        type="button"
      >
        <Braces className="h-3 w-3" /> {t("forms.email.insertVariable")}
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 rounded-md border border-border bg-popover text-popover-foreground shadow-md p-1 z-50 max-h-64 overflow-y-auto">
          <p className="px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("forms.email.contactFields")}</p>
          {MERGE_FIELDS.filter(f => !f.key.startsWith("sender_")).map(f => (
            <button
              key={f.key}
              onClick={() => { onPick(f); setOpen(false); }}
              className="flex items-center justify-between w-full rounded-md px-2.5 py-1.5 text-sm hover:bg-muted/50 transition text-left"
            >
              <span>{t(f.labelKey)}</span>
              <code className="text-[10px] text-muted-foreground">{`{{${f.key}}}`}</code>
            </button>
          ))}
          <div className="h-px bg-border my-1" />
          <p className="px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("forms.email.senderFields")}</p>
          {MERGE_FIELDS.filter(f => f.key.startsWith("sender_")).map(f => (
            <button
              key={f.key}
              onClick={() => { onPick(f); setOpen(false); }}
              className="flex items-center justify-between w-full rounded-md px-2.5 py-1.5 text-sm hover:bg-muted/50 transition text-left"
            >
              <span>{t(f.labelKey)}</span>
              <code className="text-[10px] text-muted-foreground">{`{{${f.key}}}`}</code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Only Google Workspace users get the "Open in Google Workspace" hand-off;
 * Microsoft 365 users (sign-in platform or a connected Microsoft account)
 * never see it. Rendered inside DialogContent, so the integrations query in
 * useAccounts only runs while the dialog is open — the dialog itself is
 * always mounted by several pages. `?provider=` flips the simulated sign-in
 * for Lovable demos only.
 */
function OpenInGmailButton({ getHref }: { getHref: () => string }) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const providerOverride = (() => {
    if (!isLovablePreview() || typeof window === "undefined") return undefined;
    const p = new URLSearchParams(window.location.search).get("provider");
    return p === "microsoft" || p === "google" ? p : undefined;
  })();
  const { accounts } = useAccounts(providerOverride);
  const signInPlatform = providerOverride
    ?? (currentUser?.oauthPlatform === "microsoft" ? "microsoft" : currentUser?.oauthPlatform === "google" ? "google" : undefined);
  const hasMicrosoftAccount = accounts.some(
    a => a.platform === UserIntegrationPlatform.Microsoft && a.connected
  );
  const hasGoogleAccount = accounts.some(
    a => a.platform === UserIntegrationPlatform.GoogleCalendar && a.connected
  );
  const isGoogleUser =
    signInPlatform === "microsoft" || hasMicrosoftAccount
      ? false
      : signInPlatform === "google" || hasGoogleAccount;

  if (!isGoogleUser) return null;
  return (
    <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5" asChild>
      <a href={getHref()} target="_blank" rel="noopener noreferrer">
        <ExternalLink className="h-3.5 w-3.5" />
        {t("forms.email.openInGoogle")}
      </a>
    </Button>
  );
}

export default function ComposeEmailDialog({ open, onOpenChange, recipients: initialRecipients, initialSubject, initialBody, initialCcRecipients, draftKey, onSaveDraft }: ComposeEmailDialogProps) {
  const { t } = useTranslation();
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const { currentUser: backendUser } = useCurrentUser();

  // Dual-mode email signature (backend in prod, localStorage in preview),
  // reactive to Settings page edits. Centralized in the hook.
  const userSignature = useEffectiveEmailSignature();

  // Build sender overrides from backend user (replaces dummy data in merge fields)
  const senderOverrides = useMemo(() => {
    if (!backendUser) return undefined;
    const name = [backendUser.givenName, backendUser.surname].filter(Boolean).join(' ');
    const workspace = backendUser.workspaces?.find(w => String(w.id) === String(workspaceId))
      ?? backendUser.workspaces?.[0];
    return {
      sender_full_name: name || currentUser.fullName,
      sender_email: backendUser.email || currentUser.email,
      sender_company: workspace?.companyName || workspace?.name || '',
    };
  }, [backendUser, workspaceId]);

  const [sendEmailsMutation] = useSendEmailsMutation();

  // ── Template API ─────────────────────────────────────────────────────────
  const numericWorkspaceId = workspaceId ? Number(workspaceId) : null;
  // Gated on `open`: this dialog is always mounted by several pages (PeoplePage
  // mounts two instances, plus AppLayout/PersonDetailSheet/CellContextMenu), so
  // an ungated network-only query fires N copies of GetEmailTemplates on every
  // page load. With fetchPolicy network-only, un-skipping on open still fetches
  // fresh data from the server.
  const { data: templatesData, refetch: refetchTemplates } = useGetEmailTemplatesQuery({
    variables: { workspaceId: numericWorkspaceId! },
    skip: preview || !numericWorkspaceId || !open,
    fetchPolicy: "network-only",
  });
  const [createEmailTemplateMutation] = useCreateEmailTemplateMutation();
  const [updateEmailTemplateMutation] = useUpdateEmailTemplateMutation();
  const [deleteEmailTemplateMutation] = useDeleteEmailTemplateMutation();

  // Map API templates → local format
  const apiTemplates: EmailTemplate[] = ((templatesData?.emailTemplates ?? []) as any[]).map((t: any) => ({
    id: t.id,
    name: t.title ?? "",
    subject: t.subject ?? "",
    body: t.body ?? "",
    isCustom: true,
    attachmentUrls: (t.attachmentUrls ?? []) as string[],
  }));

  const [sending, setSending] = useState(false);
  // URLs already on S3 from a selected template — merged with new uploads on send
  const [templateAttachmentUrls, setTemplateAttachmentUrls] = useState<string[]>([]);
  const [activeRecipients, setActiveRecipients] = useState<Recipient[]>(initialRecipients);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [ccRecipients, setCcRecipients] = useState<Recipient[]>(initialCcRecipients ?? []);
  const [ccOpen, setCcOpen] = useState((initialCcRecipients ?? []).length > 0);
  const [ccInput, setCcInput] = useState("");
  const [bccRecipients, setBccRecipients] = useState<Recipient[]>([]);
  const [bccOpen, setBccOpen] = useState(false);
  const [bccInput, setBccInput] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  // No pre-seeded templates: the "Choose a template" pulldown should only ever
  // appear once the user has actually saved one. Preview-mode templates are
  // persisted so they survive dialog unmounts and other dialog instances.
  const [customTemplates, setCustomTemplatesState] = useState<EmailTemplate[]>(() =>
    readPreviewTemplates(workspaceId)
  );
  const setCustomTemplates = useCallback(
    (updater: EmailTemplate[] | ((prev: EmailTemplate[]) => EmailTemplate[])) => {
      setCustomTemplatesState(prev => {
        const next = typeof updater === "function" ? (updater as (p: EmailTemplate[]) => EmailTemplate[])(prev) : updater;
        writePreviewTemplates(workspaceId, next);
        return next;
      });
    },
    [workspaceId]
  );
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState("");
  const [deleteConfirmTemplateId, setDeleteConfirmTemplateId] = useState<string | null>(null);
  const [insertTarget, setInsertTarget] = useState<"subject" | "body">("body");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Draft context resolved once per open (so removing a recipient mid-edit
  // doesn't move the draft to a different key), and whether this open
  // restored a saved draft.
  const draftKeyRef = useRef<string | null>(null);
  const restoredDraftRef = useRef(false);

  // Sync activeRecipients when dialog opens with new recipients — unless a
  // restored draft supplied its own To list.
  useEffect(() => {
    if (open && !restoredDraftRef.current) setActiveRecipients(initialRecipients);
  }, [open, initialRecipients]);

  // Keep preview-mode templates in sync across dialog instances and reopens.
  useEffect(() => {
    if (!preview) return;
    const sync = () => setCustomTemplatesState(readPreviewTemplates(workspaceId));
    if (open) sync();
    window.addEventListener(PREVIEW_TEMPLATES_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREVIEW_TEMPLATES_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [preview, open, workspaceId]);

  // User picks individual vs one shared email. Cc/Bcc apply in either mode
  // (e.g. Bcc yourself on every individual copy). Pre-filled Cc (meeting
  // follow-ups) defaults to one shared email.
  const [singleEmailMode, setSingleEmailMode] = useState((initialCcRecipients ?? []).length > 0);
  const groupSend = singleEmailMode;

  // Pre-fill subject/body/Cc only on the closed→open transition — never while
  // the dialog is open, so a parent re-render can't clobber mid-edit typing
  // (or resurrect Cc entries the user removed). A draft saved for the same
  // context wins over the initial values; storage is only read here, on open,
  // because this dialog is always mounted by several pages.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const key = draftKey || recipientsDraftKey(initialRecipients);
      draftKeyRef.current = key;
      const draft = getEmailDraft(workspaceId, key);
      if (draft) {
        restoredDraftRef.current = true;
        const cc = draft.ccRecipients ?? initialCcRecipients ?? [];
        const bcc = draft.bccRecipients ?? [];
        setSubject(draft.subject ?? "");
        setBody(draft.body ?? "");
        if (draft.toRecipients?.length) setActiveRecipients(draft.toRecipients);
        setCcRecipients(cc);
        setCcOpen(cc.length > 0);
        setBccRecipients(bcc);
        setBccOpen(bcc.length > 0);
        setSingleEmailMode(draft.groupSend ?? cc.length > 0);
      } else {
        if (initialSubject !== undefined) setSubject(initialSubject);
        if (initialBody !== undefined) setBody(initialBody);
        setCcRecipients(initialCcRecipients ?? []);
        setCcOpen((initialCcRecipients ?? []).length > 0);
        setBccRecipients([]);
        setBccOpen(false);
        setSingleEmailMode((initialCcRecipients ?? []).length > 0);
      }
      setCcInput("");
      setBccInput("");
    }
    if (!open) restoredDraftRef.current = false;
    wasOpenRef.current = open;
  }, [open, initialSubject, initialBody, initialCcRecipients, initialRecipients, draftKey, workspaceId]);

  const removeCcRecipient = (id: string) => {
    setCcRecipients(prev => prev.filter(r => r.id !== id));
  };

  const removeBccRecipient = (id: string) => {
    setBccRecipients(prev => prev.filter(r => r.id !== id));
  };

  const addBccFromInput = () => {
    const email = bccInput.trim().replace(/,$/, "");
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("forms.email.invalidCc"));
      return;
    }
    const key = email.toLowerCase();
    if (
      bccRecipients.some(r => r.email.toLowerCase() === key) ||
      ccRecipients.some(r => r.email.toLowerCase() === key) ||
      activeRecipients.some(r => r.email.toLowerCase() === key)
    ) {
      toast.error(t("forms.email.duplicateRecipient"));
      return;
    }
    setBccRecipients(prev => [...prev, { id: email, name: getDisplayName(null, null, email), email }]);
    setBccInput("");
  };

  const addCcFromInput = () => {
    const email = ccInput.trim().replace(/,$/, "");
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("forms.email.invalidCc"));
      return;
    }
    const key = email.toLowerCase();
    if (ccRecipients.some(r => r.email.toLowerCase() === key) || activeRecipients.some(r => r.email.toLowerCase() === key)) {
      toast.error(t("forms.email.duplicateRecipient"));
      return;
    }
    setCcRecipients(prev => [...prev, { id: email, name: getDisplayName(null, null, email), email }]);
    setCcInput("");
  };

  // No default template seeding: the "Choose a template" picker should only
  // appear once the user has saved a template of their own.

  const removeRecipient = (id: string) => {
    setActiveRecipients(prev => {
      const updated = prev.filter(r => r.id !== id);
      // Adjust previewIndex if it would be out of bounds
      setPreviewIndex(idx => Math.min(idx, Math.max(0, updated.length - 1)));
      return updated;
    });
  };

  // ---- Drag & drop / menu move between To, Cc and Bcc (Gmail-style) ----
  const [draggingFrom, setDraggingFrom] = useState<RecipientField | null>(null);
  const [dropTarget, setDropTarget] = useState<RecipientField | null>(null);

  const moveRecipient = (id: string, from: RecipientField, to: RecipientField) => {
    if (from === to) return;
    const source =
      from === "to" ? activeRecipients : from === "cc" ? ccRecipients : bccRecipients;
    const found = source.find(r => r.id === id);
    if (!found) return;

    const removeFrom = (field: RecipientField) => {
      if (field === "to") removeRecipient(id);
      else if (field === "cc") removeCcRecipient(id);
      else removeBccRecipient(id);
    };
    const addTo = (field: RecipientField) => {
      const dedupe = (prev: Recipient[]) =>
        prev.some(r => r.email.toLowerCase() === found.email.toLowerCase())
          ? prev
          : [...prev, found];
      if (field === "to") setActiveRecipients(dedupe);
      else if (field === "cc") { setCcOpen(true); setCcRecipients(dedupe); }
      else { setBccOpen(true); setBccRecipients(dedupe); }
    };

    removeFrom(from);
    addTo(to);
  };

  const dragProps = (field: RecipientField, id: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData("text/plain", JSON.stringify({ id, from: field }));
      e.dataTransfer.effectAllowed = "move";
      setDraggingFrom(field);
    },
    onDragEnd: () => { setDraggingFrom(null); setDropTarget(null); },
  });

  const dropProps = (field: RecipientField) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!draggingFrom || draggingFrom === field) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTarget(field);
    },
    onDragLeave: () => setDropTarget(prev => (prev === field ? null : prev)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropTarget(null);
      setDraggingFrom(null);
      try {
        const { id, from } = JSON.parse(e.dataTransfer.getData("text/plain")) as {
          id: string; from: RecipientField;
        };
        moveRecipient(id, from, field);
      } catch { /* ignore malformed drags */ }
    },
  });

  const MoveMenu = ({ field, id }: { field: RecipientField; id: string }) => {
    const targets: RecipientField[] = (["to", "cc", "bcc"] as RecipientField[]).filter(f => f !== field);
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("forms.email.moveRecipient")}
            className="text-muted-foreground hover:text-foreground transition"
            onClick={e => e.stopPropagation()}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-40 p-1">
          {targets.map(target => (
            <button
              key={target}
              type="button"
              onClick={() => moveRecipient(id, field, target)}
              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted transition"
            >
              {t("forms.email.moveTo", { field: t(`forms.email.${target}`) })}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    );
  };

  const allTemplates = useMemo(() => {
    if (!preview) return apiTemplates;
    return customTemplates;
  }, [apiTemplates, customTemplates, preview]);

  const handleTemplateSelect = (templateId: string) => {
    const template = allTemplates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setSelectedTemplateId(templateId);
      // Load template's existing S3 attachments; clear any locally-added files
      setTemplateAttachmentUrls(template.attachmentUrls ?? []);
      setAttachments([]);
    }
  };

  const insertText = (text: string) => {
    if (insertTarget === "subject") {
      const el = subjectRef.current;
      if (el) {
        const start = el.selectionStart || subject.length;
        const before = subject.slice(0, start);
        const after = subject.slice(start);
        setSubject(before + text + after);
        setTimeout(() => { el.focus(); el.setSelectionRange(start + text.length, start + text.length); }, 0);
      } else {
        setSubject(prev => prev + text);
      }
    } else {
      const el = bodyRef.current;
      if (el) {
        const start = el.selectionStart || body.length;
        const before = body.slice(0, start);
        const after = body.slice(start);
        setBody(before + text + after);
        setTimeout(() => { el.focus(); el.setSelectionRange(start + text.length, start + text.length); }, 0);
      } else {
        setBody(prev => prev + text);
      }
    }
  };

  const [variableDialogField, setVariableDialogField] = useState<MergeField | null>(null);

  const handleAttach = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) return;
    if (!preview && numericWorkspaceId) {
      try {
        const uploadedUrls = attachments.length ? await uploadEmailAttachments(attachments) : [];
        const attachmentUrls = [...uploadedUrls, ...templateAttachmentUrls];
        await createEmailTemplateMutation({
          variables: {
            input: {
              workspaceId: String(numericWorkspaceId),
              title: newTemplateName.trim(),
              subject,
              body,
              ...(attachmentUrls.length ? { attachmentUrls } : {}),
            },
          },
        });
        await refetchTemplates();
        toast.success(t("forms.email.templateSaved"));
      } catch {
        toast.error(t("forms.email.templateSaveFailed"));
      }
    } else {
      // Preview mode — local state only
      const id = `custom-${Date.now()}`;
      setCustomTemplates(prev => [...prev, { id, name: newTemplateName.trim(), subject, body, isCustom: true }]);
      toast.success(t("forms.email.templateSaved"));
    }
    setShowNewTemplate(false);
    setNewTemplateName("");
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const isApiTemplate = !preview && apiTemplates.some(t => t.id === templateId);
    if (isApiTemplate && numericWorkspaceId) {
      try {
        await deleteEmailTemplateMutation({
          variables: { input: { id: templateId, workspaceId: String(numericWorkspaceId) } },
        });
        await refetchTemplates();
        toast.success(t("forms.email.templateDeleted"));
      } catch {
        toast.error(t("forms.email.templateDeleteFailed"));
      }
    } else {
      setCustomTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success(t("forms.email.templateDeleted"));
    }
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId("");
      setTemplateAttachmentUrls([]);
    }
  };

  const handleRenameTemplate = (templateId: string) => {
    if (!editingTemplateName.trim()) return;
    setCustomTemplates(prev => prev.map(t => t.id === templateId ? { ...t, name: editingTemplateName.trim() } : t));
    setEditingTemplateId(null);
    setEditingTemplateName("");
    toast.success(t("forms.email.templateRenamed"));
  };

  const handleUpdateTemplateContent = async () => {
    if (!selectedTemplateId) return;
    const isApiTemplate = !preview && apiTemplates.some(t => t.id === selectedTemplateId);
    if (isApiTemplate && numericWorkspaceId) {
      try {
        const uploadedUrls = attachments.length ? await uploadEmailAttachments(attachments) : [];
        const attachmentUrls = [...uploadedUrls, ...templateAttachmentUrls];
        const existingTemplate = apiTemplates.find(t => t.id === selectedTemplateId);
        await updateEmailTemplateMutation({
          variables: {
            input: {
              id: selectedTemplateId,
              workspaceId: String(numericWorkspaceId),
              title: existingTemplate?.name,
              subject,
              body,
              ...(attachmentUrls.length ? { attachmentUrls } : {}),
            },
          },
        });
        await refetchTemplates();
        toast.success(t("forms.email.templateUpdated"));
      } catch {
        toast.error(t("forms.email.templateUpdateFailed"));
      }
      return;
    }
    // Preview-mode custom template
    setCustomTemplates(prev => prev.map(t => t.id === selectedTemplateId ? { ...t, subject, body } : t));
    toast.success(t("forms.email.templateUpdated"));
  };

  const selectedTemplateChanged = useMemo(() => {
    if (!selectedTemplateId) return false;
    const t = allTemplates.find(t => t.id === selectedTemplateId);
    return t ? (t.subject !== subject || t.body !== body) : false;
  }, [selectedTemplateId, allTemplates, subject, body]);

  // In group mode a single email goes to everyone, so per-recipient merge
  // fields only resolve when there's exactly one To recipient; otherwise they
  // fall back to their defaults (sender fields always resolve).
  const resolveGroupTemplate = (text: string, rs: Recipient[]): string => {
    if (rs.length === 1) return resolveTemplate(text, rs[0], senderOverrides);
    const values: Record<string, string> = {};
    for (const f of MERGE_FIELDS) {
      const override = senderOverrides?.[f.key];
      values[f.key] = override !== undefined ? override : f.defaultFallback;
    }
    return resolveMergeVars(text, values);
  };

  const handleSendToRecipients = async (recipientsToSend: typeof activeRecipients) => {
    if (recipientsToSend.length === 0) { toast.error(t("forms.email.needRecipient")); return; }
    if (!subject.trim()) { toast.error(t("forms.email.needSubject")); return; }
    if (!body.trim()) { toast.error(t("forms.email.needBody")); return; }
    const count = recipientsToSend.length;

    if (preview || !workspaceId) {
      if (groupSend) {
        clearEmailDraft(workspaceId, draftKeyRef.current);
        toast.success(t("forms.email.sentGroup", { count, cc: ccRecipients.length }));
        onOpenChange(false);
        setSubject(""); setBody(""); setAttachments([]); setTemplateAttachmentUrls([]); setSelectedTemplateId("");
        setCcRecipients([]); setCcInput(""); setBccRecipients([]); setBccInput("");
        setIsPreviewMode(false); setPreviewIndex(0);
        return;
      }
      toast.success(t("forms.email.sentPreview", { count }));
      // Remove sent recipients from the active list
      if (count < activeRecipients.length) {
        const sentIds = new Set(recipientsToSend.map(r => r.id));
        removeRecipient && sentIds.forEach(id => removeRecipient(id));
      } else {
        clearEmailDraft(workspaceId, draftKeyRef.current);
        onOpenChange(false);
        setSubject(""); setBody(""); setAttachments([]); setTemplateAttachmentUrls([]); setSelectedTemplateId("");
        setIsPreviewMode(false); setPreviewIndex(0);
      }
      return;
    }

    setSending(true);
    try {
      const fromEmail = backendUser?.email || currentUser.email;
      const fromName = backendUser?.preferredName || backendUser?.givenName || currentUser.name;

      // Upload any locally-added files, then merge with template S3 URLs
      const uploadedUrls = attachments.length ? await uploadEmailAttachments(attachments) : [];
      const attachmentUrls = [...uploadedUrls, ...templateAttachmentUrls];

      const bodyWithSig = appendSignatureToBody(body, userSignature);

      let ok: boolean;
      if (groupSend) {
        // One shared email: everyone in To + Cc sees each other on the thread
        const result = await sendEmailsMutation({
          variables: {
            workspaceId: Number(workspaceId),
            fromEmail,
            fromName,
            sendIndividually: false,
            toEmails: recipientsToSend.map(r => r.email),
            ccEmails: ccRecipients.map(r => r.email),
            bccEmails: bccRecipients.map(r => r.email),
            subject: resolveGroupTemplate(subject, recipientsToSend),
            body: stripSignatureMarker(resolveGroupTemplate(bodyWithSig, recipientsToSend)),
            ...(attachmentUrls.length ? { attachmentUrls } : {}),
          },
        });
        ok = !!result.data?.sendEmails?.ok;
      } else {
        // Build per-recipient resolved emails with merge fields applied
        const resolvedEmails = recipientsToSend.map(r => ({
          to: r.email,
          subject: resolveTemplate(subject, r, senderOverrides),
          body: stripSignatureMarker(resolveTemplate(bodyWithSig, r, senderOverrides)),
        }));

        const result = await sendEmailsMutation({
          variables: {
            workspaceId: Number(workspaceId),
            fromEmail,
            fromName,
            sendIndividually: true,
            resolvedEmails,
            ...(ccRecipients.length ? { ccEmails: ccRecipients.map(r => r.email) } : {}),
            ...(bccRecipients.length ? { bccEmails: bccRecipients.map(r => r.email) } : {}),
            ...(attachmentUrls.length ? { attachmentUrls } : {}),
          },
        });
        ok = !!result.data?.sendEmails?.ok;
      }

      if (ok) {
        clearEmailDraft(workspaceId, draftKeyRef.current);
        toast.success(groupSend ? t("forms.email.sentOne") : t("forms.email.sentCount", { count }));
      } else {
        toast.error(t("forms.email.sendFailed"));
      }
    } catch (error) {
      console.error("Failed to send emails:", error);
      toast.error(t("forms.email.sendFailed"));
    } finally {
      setSending(false);
      onOpenChange(false);
      setSubject(""); setBody(""); setAttachments([]); setTemplateAttachmentUrls([]); setSelectedTemplateId("");
      setCcRecipients([]); setCcInput(""); setBccRecipients([]); setBccInput("");
      setIsPreviewMode(false); setPreviewIndex(0);
    }
  };

  const handleSend = () => handleSendToRecipients(activeRecipients);
  const handleSendSingle = () => {
    const recipient = activeRecipients[previewIndex];
    if (recipient) handleSendToRecipients([recipient]);
  };

  const previewRecipient = activeRecipients[previewIndex] || activeRecipients[0];
  const previewSubject = groupSend
    ? resolveGroupTemplate(subject, activeRecipients)
    : previewRecipient ? resolveTemplate(subject, previewRecipient, senderOverrides) : subject;
  const previewBody = groupSend
    ? resolveGroupTemplate(body, activeRecipients)
    : previewRecipient ? resolveTemplate(body, previewRecipient, senderOverrides) : body;

  const recipientLabel = activeRecipients.length === 1
    ? `${activeRecipients[0].name} <${activeRecipients[0].email}>`
    : groupSend
      ? t("forms.email.sendingOne", { total: activeRecipients.length })
      : t("forms.email.sendingIndividual", { total: activeRecipients.length });

  const gmailComposeUrl = () => {
    const params = new URLSearchParams({
      view: "cm",
      fs: "1",
      to: activeRecipients.map(r => r.email).join(","),
      su: resolveGroupTemplate(subject, activeRecipients),
      body: resolveGroupTemplate(body, activeRecipients),
    });
    if (ccRecipients.length) params.set("cc", ccRecipients.map(r => r.email).join(","));
    if (bccRecipients.length) params.set("bcc", bccRecipients.map(r => r.email).join(","));
    return `https://mail.google.com/mail/?${params.toString()}`;
  };

  const handleSaveDraft = () => {
    if (!subject.trim() && !body.trim()) {
      toast.error(t("forms.email.draftEmpty"));
      return;
    }
    // Non-fatal if storage is unavailable: drafts are a local convenience.
    // Restored the next time the dialog opens for the same context.
    saveEmailDraft(workspaceId, {
      contextKey: draftKeyRef.current,
      toRecipients: activeRecipients,
      ccRecipients,
      bccRecipients,
      groupSend,
      subject,
      body,
    });
    onSaveDraft?.({ subject, body });
    toast.success(t("forms.email.draftSaved"));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" /> {t("forms.email.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4 flex-1 overflow-y-auto min-h-0">
          {/* To field - collapsible dropdown */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.email.to")}</label>
            {activeRecipients.length > 1 ? (
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <select
                  aria-label={t("forms.email.sendMode")}
                  value={groupSend ? "group" : "individual"}
                  onChange={e => setSingleEmailMode(e.target.value === "group")}
                  className="w-full appearance-none rounded-md border border-input bg-muted/30 pl-8 pr-8 py-2 text-sm text-foreground hover:bg-muted/50 transition disabled:cursor-not-allowed"
                >
                  <option value="individual">{t("forms.email.sendingIndividual", { total: activeRecipients.length })}</option>
                  <option value="group">{t("forms.email.sendingOne", { total: activeRecipients.length })}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              </div>
            ) : (
            <button
              type="button"
              onClick={() => setRecipientsOpen(prev => !prev)}
              className="flex items-center gap-2 w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50 transition"
            >
              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-foreground flex-1 text-left">{recipientLabel}</span>
              <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", recipientsOpen && "rotate-180")} />
            </button>
            )}
            {recipientsOpen && (
              <div
                {...dropProps(FIELD.to)}
                className={cn(
                  "rounded-md border border-input bg-background max-h-[180px] overflow-y-auto divide-y divide-border transition",
                  dropTarget === "to" && "border-accent ring-1 ring-accent"
                )}
              >
                {activeRecipients.map(r => (
                  <div
                    key={r.id}
                    {...dragProps(FIELD.to, r.id)}
                    title={t("forms.email.dragToCcBcc")}
                    className="flex items-center gap-2.5 px-3 py-2 group hover:bg-muted/30 transition cursor-grab active:cursor-grabbing"
                  >
                    <UserAvatar name={r.name} size="xs" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-tight">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate leading-tight">{r.email}</p>
                    </div>
                    <MoveMenu field="to" id={r.id} />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeRecipient(r.id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 hover:text-destructive transition shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {activeRecipients.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-3">{t("forms.email.noRecipients")}</p>
                )}
              </div>
            )}
          </div>

          {/* Cc / Bcc — toggling links like Gmail / Outlook */}
          <div className="flex items-center gap-3 -mt-2">
            <button
              type="button"
              onClick={() => {
                setCcOpen(prev => {
                  if (prev) { setCcRecipients([]); setCcInput(""); }
                  return !prev;
                });
              }}
              className={cn(
                "text-xs underline underline-offset-2 transition hover:text-foreground",
                ccOpen ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {t("forms.email.cc")}
            </button>
            <button
              type="button"
              onClick={() => {
                setBccOpen(prev => {
                  if (prev) { setBccRecipients([]); setBccInput(""); }
                  return !prev;
                });
              }}
              className={cn(
                "text-xs underline underline-offset-2 transition hover:text-foreground",
                bccOpen ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {t("forms.email.bcc")}
            </button>
          </div>

          {ccOpen && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.email.cc")}</label>
              <div
                {...dropProps(FIELD.cc)}
                className={cn(
                  "flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 transition",
                  dropTarget === "cc" && "border-accent ring-1 ring-accent"
                )}
              >
                {ccRecipients.map(r => (
                  <span
                    key={r.id}
                    {...dragProps(FIELD.cc, r.id)}
                    title={t("forms.email.dragToToBcc")}
                    className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs cursor-grab active:cursor-grabbing"
                  >
                    {r.email}
                    <MoveMenu field="cc" id={r.id} />
                    <button type="button" onClick={() => removeCcRecipient(r.id)} className="text-muted-foreground hover:text-destructive transition">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={ccInput}
                  onChange={e => setCcInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addCcFromInput(); }
                    if (e.key === "Backspace" && !ccInput && ccRecipients.length) removeCcRecipient(ccRecipients[ccRecipients.length - 1].id);
                  }}
                  onBlur={() => { if (ccInput.trim()) addCcFromInput(); }}
                  placeholder={ccRecipients.length ? "" : t("forms.email.addEmailPlaceholder")}
                  autoFocus
                  className="flex-1 min-w-[140px] h-6 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              {groupSend && (
                <p className="text-[11px] text-muted-foreground">
                  {t("forms.email.groupSendNote")}
                </p>
              )}
            </div>
          )}

          {bccOpen && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.email.bcc")}</label>
              <div
                {...dropProps(FIELD.bcc)}
                className={cn(
                  "flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 transition",
                  dropTarget === "bcc" && "border-accent ring-1 ring-accent"
                )}
              >
                {bccRecipients.map(r => (
                  <span
                    key={r.id}
                    {...dragProps(FIELD.bcc, r.id)}
                    title={t("forms.email.dragToToCc")}
                    className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs cursor-grab active:cursor-grabbing"
                  >
                    {r.email}
                    <MoveMenu field="bcc" id={r.id} />
                    <button type="button" onClick={() => removeBccRecipient(r.id)} className="text-muted-foreground hover:text-destructive transition">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={bccInput}
                  onChange={e => setBccInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addBccFromInput(); }
                    if (e.key === "Backspace" && !bccInput && bccRecipients.length) removeBccRecipient(bccRecipients[bccRecipients.length - 1].id);
                  }}
                  onBlur={() => { if (bccInput.trim()) addBccFromInput(); }}
                  placeholder={bccRecipients.length ? "" : t("forms.email.addEmailPlaceholder")}
                  autoFocus
                  className="flex-1 min-w-[140px] h-6 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
          )}

          {/* Template selector row */}
          <div className="flex items-center gap-2">
            {allTemplates.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[200px] h-9 text-xs justify-between font-normal">
                  <span className={cn(!selectedTemplateId && "text-muted-foreground")}>
                    {selectedTemplateId ? allTemplates.find(tpl => tpl.id === selectedTemplateId)?.name : t("forms.email.chooseTemplate")}
                  </span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground rotate-90" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 p-1 max-h-64 overflow-y-auto">
                {allTemplates.map(t => (
                  <div key={t.id} className="group flex items-center justify-between rounded-md hover:bg-muted/50 transition">
                    {editingTemplateId === t.id ? (
                      <div className="flex items-center gap-1 flex-1 px-1 py-0.5">
                        <input
                          value={editingTemplateName}
                          onChange={e => setEditingTemplateName(e.target.value)}
                          className="h-7 text-xs flex-1 bg-transparent outline-none px-1.5"
                          autoFocus
                          onKeyDown={e => { if (e.key === "Enter") handleRenameTemplate(t.id); if (e.key === "Escape") setEditingTemplateId(null); }}
                          onClick={e => e.stopPropagation()}
                          onBlur={() => handleRenameTemplate(t.id)}
                        />
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleTemplateSelect(t.id)}
                          className={cn(
                            "flex-1 text-left px-2.5 py-1.5 text-sm truncate",
                            selectedTemplateId === t.id && "font-medium"
                          )}
                        >
                          {t.name}
                        </button>
                        <div className="flex items-center gap-0.5 pr-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingTemplateId(t.id); setEditingTemplateName(t.name); }}
                            className="p-1 text-muted-foreground hover:text-foreground transition"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmTemplateId(t.id); }}
                            className="p-1 text-muted-foreground hover:text-destructive transition"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            )}

            {/* Delete template confirmation */}
            <AlertDialog open={!!deleteConfirmTemplateId} onOpenChange={(open) => { if (!open) setDeleteConfirmTemplateId(null); }}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("forms.email.deleteTemplateTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("forms.email.deleteTemplateConfirm", {
                      name: allTemplates.find(tpl => tpl.id === deleteConfirmTemplateId)?.name ?? "",
                    })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => { if (deleteConfirmTemplateId) handleDeleteTemplate(deleteConfirmTemplateId); setDeleteConfirmTemplateId(null); }}
                  >
                    {t("common.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Save / Update template buttons */}
            {showNewTemplate ? (
              <div className="flex items-center gap-1.5 flex-1">
                <Input
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  placeholder={t("forms.email.templateNamePlaceholder")}
                  className="h-9 text-xs flex-1"
                  autoFocus
                  onKeyDown={e => { if (e.key === "Enter") handleSaveAsTemplate(); if (e.key === "Escape") setShowNewTemplate(false); }}
                />
                <Button size="sm" variant="outline" className="h-9 text-xs" onClick={handleSaveAsTemplate}>
                  <Check className="h-3 w-3 mr-1" /> {t("common.save")}
                </Button>
                <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={() => setShowNewTemplate(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                {(subject.trim() || body.trim()) && (
                  <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setShowNewTemplate(true)}>
                    <Plus className="h-3 w-3 mr-1" /> {t("forms.email.saveAsTemplate")}
                  </Button>
                )}
                {selectedTemplateChanged && selectedTemplateId && (
                  <Button size="sm" variant="outline" className="h-9 text-xs" onClick={handleUpdateTemplateContent}>
                    <Check className="h-3 w-3 mr-1" /> {t("forms.email.updateTemplate")}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.email.subject")}</label>
            <Input
              ref={subjectRef}
              value={isPreviewMode ? previewSubject : subject}
              onChange={e => {
                if (isPreviewMode) {
                  // In preview mode, update the base subject (reverse-resolve isn't practical, so just set directly)
                  setSubject(e.target.value);
                  setIsPreviewMode(false);
                } else {
                  setSubject(e.target.value);
                }
              }}
              onFocus={() => setInsertTarget("subject")}
              placeholder={t("forms.email.subjectPlaceholder")}
              className="h-9"
            />
          </div>

          {/* Body */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.email.message")}</label>
              <div className="flex items-center gap-1">
                <VariableInsertMenu onPick={(f) => setVariableDialogField(f)} />
                {/* Attach */}
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground hover:bg-muted/50" onClick={handleAttach}>
                  <Paperclip className="h-3 w-3" /> {t("forms.email.attach")}
                </Button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
              </div>
            </div>
            <div className="relative">
              <Textarea
                ref={bodyRef}
                value={isPreviewMode ? previewBody : body}
                onChange={e => {
                  if (isPreviewMode) {
                    setBody(e.target.value);
                    setIsPreviewMode(false);
                  } else {
                    setBody(e.target.value);
                  }
                }}
                onFocus={() => setInsertTarget("body")}
                placeholder={t("forms.email.bodyPlaceholder")}
                className={cn("min-h-[200px] resize-none text-sm", userSignature && "rounded-b-none border-b-0")}
              />
            </div>
            {userSignature && (
              <div className="rounded-b-md border border-input border-t-0 bg-muted/20 px-3 py-2">
                <div className="border-t border-dashed border-border pt-2">
                  <div
                    className="prose prose-sm max-w-none text-sm text-foreground [&_a]:text-primary [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(userSignature) }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Attachments — local files + template S3 URLs */}
          {(attachments.length > 0 || templateAttachmentUrls.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((file, i) => (
                <div key={`file-${i}`} className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-xs">
                  <FileText className="h-3 w-3 text-muted-foreground" />
                  <span className="max-w-[120px] truncate">{file.name}</span>
                  <span className="text-muted-foreground">{t("forms.email.fileSizeKb", { size: (file.size / 1024).toFixed(0) })}</span>
                  <button onClick={() => removeAttachment(i)} className="ml-0.5 text-muted-foreground hover:text-destructive transition">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {templateAttachmentUrls.map((url, i) => {
                const fileName = url.split("/").pop()?.split("?")[0] || t("forms.email.attachmentN", { n: i + 1 });
                return (
                  <div key={`url-${i}`} className="flex items-center gap-1.5 rounded-md border border-border bg-accent/10 px-2.5 py-1.5 text-xs">
                    <FileText className="h-3 w-3 text-accent" />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="max-w-[120px] truncate hover:underline text-accent">
                      {fileName}
                    </a>
                    <button
                      onClick={() => setTemplateAttachmentUrls(prev => prev.filter((_, idx) => idx !== i))}
                      className="ml-0.5 text-muted-foreground hover:text-destructive transition"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Preview mode controls — group mode has no per-recipient variants */}
          {isPreviewMode && groupSend && activeRecipients.length > 1 && (
            <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">{t("forms.email.oneEmailTo", { total: activeRecipients.length })}</p>
                <p className="text-xs text-muted-foreground">{t("forms.email.ccCount", { total: ccRecipients.length })}</p>
              </div>
            </div>
          )}

          {isPreviewMode && !groupSend && activeRecipients.length > 1 && (
            <div className="flex items-center justify-between rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
              <div className="flex items-center gap-3">
                <UserAvatar name={previewRecipient.name} size="sm" />
                <div>
                  <p className="text-sm font-medium">{previewRecipient.name}</p>
                  <p className="text-xs text-muted-foreground">{previewRecipient.email}{previewRecipient.company ? ` · ${previewRecipient.company}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  disabled={previewIndex === 0}
                  onClick={() => setPreviewIndex(prev => prev - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs font-medium text-muted-foreground min-w-[60px] text-center">
                  {t("forms.email.previewIndex", { current: previewIndex + 1, total: activeRecipients.length })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  disabled={previewIndex === activeRecipients.length - 1}
                  onClick={() => setPreviewIndex(prev => prev + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {isPreviewMode && activeRecipients.length === 1 && (
            <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
              <UserAvatar name={previewRecipient.name} size="sm" />
              <div>
                <p className="text-sm font-medium">{previewRecipient.name}</p>
                <p className="text-xs text-muted-foreground">{previewRecipient.email}{previewRecipient.company ? ` · ${previewRecipient.company}` : ""}</p>
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-9 text-xs gap-1.5"
                onClick={handleSaveDraft}
              >
                <Save className="h-3.5 w-3.5" />
                {t("forms.email.saveDraft")}
              </Button>
              <OpenInGmailButton getHref={gmailComposeUrl} />
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              {isPreviewMode && !groupSend && activeRecipients.length > 1 ? (
                <>
                  <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5" onClick={handleSendSingle} disabled={sending}>
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {t("forms.email.send")}
                  </Button>
                  <Button size="sm" className="h-9 text-xs gap-1.5" onClick={handleSend} disabled={sending}>
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {t("forms.email.sendAll", { total: activeRecipients.length })}
                   </Button>
                </>
              ) : (
                <Button size="sm" className="h-9 text-xs gap-1.5" onClick={handleSend} disabled={sending}>
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {t("forms.email.send")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      {variableDialogField && (
        <InsertVariableDialog
          open={!!variableDialogField}
          onOpenChange={(open) => { if (!open) setVariableDialogField(null); }}
          variableKey={variableDialogField.key}
          variableLabel={t(variableDialogField.labelKey)}
          defaultFallback={variableDialogField.defaultFallback}
          onInsert={insertText}
        />
      )}
    </Dialog>
  );
}
